
import { useEffect, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
	restoreFiltersToURL,
	convertFiltersForPage,
	syncFiltersToStorage,
	resetFilters,
	isValidFilterPage,
	isSettingsPage
} from '../utils/filter-presistence'

export function useFilterPersistence() {
	const location = useLocation()
	const [searchParams, setSearchParams] = useSearchParams()
	const prevAccountIdRef = useRef<string | null>(null)
	const prevAssetIdRef = useRef<string | null>(null)
	const prevPathnameRef = useRef<string>(location.pathname)
	const searchParamsRef = useRef<URLSearchParams>(searchParams)
	const isMountedRef = useRef(true)
	const lastSyncedSearchParamsRef = useRef<string>('')
	const lastRestoredPathnameRef = useRef<string>('')
	const prevPathnameForConversionRef = useRef<string>('')
	const restoredForPathnameRef = useRef<string>('')
	
	useEffect(() => {
		searchParamsRef.current = searchParams
	}, [searchParams])

	const pathParts = location.pathname.split('/')
	const accountIdIndex = pathParts.findIndex((part) => part === 'workspace')
	const accountId = accountIdIndex !== -1 ? pathParts[accountIdIndex + 1] : null
	const assetIdIndex = pathParts.findIndex((part) => part === 'asset')
	const assetId = assetIdIndex !== -1 ? pathParts[assetIdIndex + 1] : null

	
	useEffect(() => {
		isMountedRef.current = true
		return () => {
			isMountedRef.current = false
		}
	}, [])

	
	useEffect(() => {
		if (prevAccountIdRef.current === null && accountId) {
			prevAccountIdRef.current = accountId
		}
		if (prevAssetIdRef.current === null && assetId) {
			prevAssetIdRef.current = assetId
		}
	}, [accountId, assetId])

	
	useEffect(() => {
		const currentPathname = location.pathname
		const pathnameChanged = prevPathnameForConversionRef.current !== currentPathname

		if (
			isValidFilterPage(currentPathname) &&
			isMountedRef.current &&
			pathnameChanged &&
			lastRestoredPathnameRef.current === currentPathname
		) {
			const windowParams = new URLSearchParams(window.location.search)
			const routerParams = searchParamsRef.current

			const hasCampaignInWindow = windowParams.get('campaign') || windowParams.get('breadCampaigns')
			const hasCampaignInRouter = routerParams.get('campaign') || routerParams.get('breadCampaigns')

			if (hasCampaignInWindow && !hasCampaignInRouter) {
				const currentParams = windowParams
				const convertedParams = convertFiltersForPage(currentParams, currentPathname)
				if (convertedParams && isMountedRef.current) {
					const currentRouterParams = new URLSearchParams(searchParamsRef.current)
					if (convertedParams.toString() !== currentRouterParams.toString()) {
						setSearchParams(convertedParams, { replace: true })
					}
				}
			}
			prevPathnameForConversionRef.current = currentPathname
		}
	}, [location.pathname, setSearchParams])  

	
	useEffect(() => {
		if (isValidFilterPage(location.pathname) && isMountedRef.current) {
			if (lastRestoredPathnameRef.current === location.pathname) {
				return
			}

			const currentParams = new URLSearchParams(window.location.search)
			const currentRouterParams = new URLSearchParams(searchParamsRef.current)

			const restoredParams = restoreFiltersToURL(currentParams, location.pathname)
			if (restoredParams && isMountedRef.current) {
				if (restoredParams.toString() !== currentRouterParams.toString()) {
					setSearchParams(restoredParams, { replace: true })
				}
				lastRestoredPathnameRef.current = location.pathname
				restoredForPathnameRef.current = location.pathname
			} else {
				lastRestoredPathnameRef.current = location.pathname
				restoredForPathnameRef.current = location.pathname
			}
		}
	}, [location.pathname, setSearchParams]) 

	useEffect(() => {
		if (isValidFilterPage(location.pathname) && isMountedRef.current) {
			if (restoredForPathnameRef.current !== location.pathname) {
				return
			}
			const currentParamsString = searchParams.toString()
			if (lastSyncedSearchParamsRef.current === currentParamsString) {
				return
			}
			syncFiltersToStorage(searchParams, location.pathname)
			lastSyncedSearchParamsRef.current = currentParamsString
		}
	}, [searchParams, location.pathname])

	useEffect(() => {
		if (
			isMountedRef.current &&
			prevAccountIdRef.current !== null &&
			prevAccountIdRef.current !== accountId &&
			accountId !== null
		) {
			const resetParams = resetFilters('all', searchParams)
			if (isMountedRef.current) {
				setSearchParams(resetParams, { replace: true })
			}
			lastRestoredPathnameRef.current = ''
		}
		if (accountId !== null) {
			prevAccountIdRef.current = accountId
		}
	}, [accountId, searchParams, setSearchParams])

	useEffect(() => {
		if (
			isMountedRef.current &&
			prevAssetIdRef.current !== null &&
			prevAssetIdRef.current !== assetId &&
			assetId !== null &&
			isValidFilterPage(location.pathname)
		) {
			const resetParams = resetFilters('breadcrumbs-only', searchParams)
			if (isMountedRef.current) {
				setSearchParams(resetParams, { replace: true })
			}
		}
		if (assetId !== null) {
			prevAssetIdRef.current = assetId
		}
	}, [assetId, location.pathname, searchParams, setSearchParams])

	useEffect(() => {
		if (isMountedRef.current) {
			prevPathnameRef.current = location.pathname
		}
	}, [location.pathname])
}
